// Stacking rules shared by every path that raises a window: opening,
// restoring, docking, and focusing.

import { getWindowStackZIndex } from "@smartmanager/shell-ui/features/windows/minimizedDockLayout";

/**
 * Windows that wait on a decision from the person: approve a device, stay
 * signed in, pay to keep using the workspace, answer a call, or answer the
 * assistant's permission request. Another window must never
 * hide one, so they form the top stacking layer and placement avoids
 * covering them.
 *
 * This is narrower than EPHEMERAL_COMPONENT_NAMES on purpose. Status cards
 * (JobStatus), calls, and pickers are transient too, but they do not block on
 * the person, and pinning them above the window the person just opened would
 * hide the work they asked for.
 */
export const ATTENTION_PROMPT_NAMES: ReadonlySet<string> = new Set([
  "DeviceApproval",
  "InactivityWarning",
  "SubscriptionRequired",
  "StripeCheckoutViewer",
  "IncomingCall",
  "PermissionRequest",
]);

export type StackedWindow = {
  id: string;
  name: string;
  isMinimized?: boolean;
  zIndex?: number;
  score?: number;
};

export const isAttentionPrompt = (entry: Pick<StackedWindow, "name">): boolean =>
  ATTENTION_PROMPT_NAMES.has(entry.name);

export const getStackZIndex = (
  entry: Pick<StackedWindow, "isMinimized" | "zIndex" | "score">,
): number =>
  getWindowStackZIndex({
    isMinimized: Boolean(entry.isMinimized),
    zIndex: entry.zIndex,
    score: entry.score,
  });

/**
 * Keeps every visible attention prompt above every visible ordinary window.
 *
 * When a raise leaves an ordinary window at or above a prompt, the visible
 * prompts are re-stacked directly above the top ordinary window, keeping
 * their relative order. Ordinary windows are never lowered, so the window
 * that was just raised stays above everything it covers except prompts.
 * Returns the same array when the rule already holds.
 */
export function keepAttentionPromptsOnTop<T extends StackedWindow>(entries: T[]): T[] {
  let ordinaryTop = Number.NEGATIVE_INFINITY;
  const prompts: Array<{ entry: T; index: number; zIndex: number }> = [];
  entries.forEach((entry, index) => {
    if (entry.isMinimized) return;
    const zIndex = getStackZIndex(entry);
    if (isAttentionPrompt(entry)) {
      prompts.push({ entry, index, zIndex });
    } else {
      ordinaryTop = Math.max(ordinaryTop, zIndex);
    }
  });
  if (prompts.length === 0 || prompts.every((prompt) => prompt.zIndex > ordinaryTop)) {
    return entries;
  }

  prompts.sort((left, right) => left.zIndex - right.zIndex || left.index - right.index);
  const liftedZIndex = new Map(
    prompts.map((prompt, rank) => [prompt.entry.id, ordinaryTop + 1 + rank]),
  );
  return entries.map((entry) => {
    const zIndex = liftedZIndex.get(entry.id);
    return zIndex === undefined ? entry : { ...entry, zIndex };
  });
}

/** Applies keepAttentionPromptsOnTop to every window's component list. */
export function keepAttentionPromptsOnTopByWindow<T extends StackedWindow>(
  componentsByWindow: Record<string, T[]>,
): Record<string, T[]> {
  return Object.fromEntries(
    Object.entries(componentsByWindow).map(([windowId, components]) => [
      windowId,
      keepAttentionPromptsOnTop(components),
    ]),
  );
}
